import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemasParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchemasList,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'
import { WorkflowComponent } from './common/workflowcomponent'
import { tempTableName } from '../utils'

const RESERVED_COLUMNS = ['geoid', 'h3', 'quadbin']

class SpatialCompositeUnsupervised extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialcompositeunsupervised',
        group: 'Statistics',
        title: 'Composite Score Unsupervised',
        description: `
      This component combines (spatial) input variables into a meaningful composite score.
      The composite score can be derived using different methods, scaling and aggregation functions and weights.
      Rows with a NULL value in any of the model predictors are dropped.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          title: 'Column with unique geographic identifier',
          allowedColumns: RESERVED_COLUMNS,
          noDefault: true
        },
        {
          name: 'scoringmethod',
          type: WorkflowComponentParamType.Selection,
          title: 'Scoring method',
          options: ['ENTROPY', 'CUSTOM_WEIGHTS', 'FIRST_PC'],
          default: 'ENTROPY'
        },
        {
          name: 'weights',
          type: WorkflowComponentParamType.ColumnNumber,
          title: 'Variables and Weights',
          excludedColumns: RESERVED_COLUMNS,
          enableReverse: true,
          default: '[]',
          showIf: [
            {
              parameter: 'scoringmethod',
              value: 'CUSTOM_WEIGHTS'
            }
          ]
        },
        {
          name: 'scaling',
          type: WorkflowComponentParamType.Selection,
          title: 'Scaling function',
          default: 'MIN_MAX_SCALER',
          options: [
            'MIN_MAX_SCALER',
            'STANDARD_SCALER',
            'RANKING',
            'DISTANCE_TO_TARGET_MIN',
            'DISTANCE_TO_TARGET_MAX',
            'DISTANCE_TO_TARGET_AVG',
            'PROPORTION'
          ],
          showIf: [
            {
              parameter: 'scoringmethod',
              value: 'CUSTOM_WEIGHTS'
            }
          ]
        },
        {
          name: 'aggregation',
          type: WorkflowComponentParamType.Selection,
          title: 'Aggregation function',
          default: 'LINEAR',
          options: ['LINEAR', 'GEOMETRIC'],
          showIf: [
            {
              parameter: 'scoringmethod',
              value: 'CUSTOM_WEIGHTS'
            }
          ]
        },
        {
          name: 'correlationvar',
          type: WorkflowComponentParamType.Column,
          dataType: [
            ColumnDataType.Boolean,
            ColumnDataType.Number,
            ColumnDataType.String,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp
          ],
          parent: 'source',
          title: 'Correlation variable',
          noDefault: true,
          excludedColumns: RESERVED_COLUMNS,
          showIf: [
            {
              parameter: 'scoringmethod',
              value: 'FIRST_PC'
            }
          ]
        },
        {
          name: 'correlationthr',
          type: WorkflowComponentParamType.Number,
          title: 'Correlation threshold',
          min: 0,
          max: 1,
          default: 0.2,
          showIf: [
            {
              parameter: 'scoringmethod',
              value: 'FIRST_PC'
            }
          ]
        },
        {
          name: 'outputformatting',
          type: WorkflowComponentParamType.Selection,
          title: 'Output formatting',
          default: 'NONE',
          options: ['NONE', 'EQUAL_INTERVALS', 'QUANTILES', 'JENKS', 'RETURN_RANGE']
        },
        {
          name: 'returnrange',
          type: WorkflowComponentParamType.Range,
          title: 'Return range',
          default: '["0.0","1.0"]',
          showIf: [
            {
              parameter: 'outputformatting',
              value: 'RETURN_RANGE'
            }
          ],
          allowExpressions: false
        },
        {
          name: 'automaticnbuckets',
          type: WorkflowComponentParamType.Boolean,
          title: 'Define number of buckets automatically',
          default: false,
          showIf: [
            {
              parameter: 'outputformatting',
              value: 'EQUAL_INTERVALS'
            }
          ]
        },
        {
          name: 'nbuckets',
          type: WorkflowComponentParamType.Number,
          title: 'Number of buckets',
          min: 2,
          default: 5,
          showIf: [
            {
              parameter: 'automaticnbuckets',
              value: false
            },
            {
              parameter: 'outputformatting',
              value: ['EQUAL_INTERVALS', 'JENKS', 'QUANTILES']
            }
          ]
        }
      ],
      [
        {
          name: 'result',
          title: 'Resulting table',
          description: 'The resulting table with the composite score.',
          type: WorkflowComponentOutputType.Table
        },
        {
          name: 'lookup',
          title: 'Lookup table',
          description: 'The lookup table with the composite score additional values.',
          type: WorkflowComponentOutputType.Table
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const source = preparedParams.source as string
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    const tablenameResult = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const tablenameLookup = `${tablenameResult}_lookup_table`
    const options = {
      scoring_method: preparedParams.scoringmethod
    }

    let selectColumnsString = '*'
    if (preparedParams.scoringmethod === 'CUSTOM_WEIGHTS') {
      const weightsArray = (preparedParams.weights && JSON.parse(preparedParams.weights as string)) as Array<Array<any>>
      const weightsSum = weightsArray?.reduce((acc, curr) => acc + curr[1], 0)
      const weights = Object.fromEntries(
        weightsArray.map(([c, w, r]) => [c, (Number(w) || 0) / (weightsSum || 1)]) // column, weight, reverse
      )
      const weightColumns = Object.entries(weightsArray).map(([_, val]) => {
        return val[2] ? `${val[0]} * -1 AS ${val[0]}` : ` ${val[0]}`
      })
      selectColumnsString = weightColumns.length ? `, ${weightColumns.join(', ')}` : ''
      selectColumnsString = `${preparedParams.indexcol}${selectColumnsString}`
      options['weights'] = Object.keys(weights).length !== 0 ? weights : undefined
      options['scaling'] = preparedParams.scaling ?? undefined
      options['aggregation'] = preparedParams.aggregation ?? undefined
    } else if (preparedParams.scoringmethod === 'FIRST_PC') {
      options['correlation_var'] = preparedParams.correlationvar ?? undefined
      options['correlation_thr'] =
        preparedParams.correlationthr !== undefined ? parseFloat(preparedParams.correlationthr as string) : undefined
    }

    if (preparedParams.outputformatting === 'RETURN_RANGE') {
      const returnrange = JSON.parse(preparedParams.returnrange as string)
      options['return_range'] = returnrange.map((v: string) => parseFloat(v))
    } else if (preparedParams.outputformatting !== 'NONE') {
      options['bucketize_method'] = preparedParams.outputformatting ?? undefined

      if (!preparedParams.automaticnbuckets) {
        options['nbuckets'] =
          preparedParams.nbuckets !== undefined ? parseInt(preparedParams.nbuckets as string) : undefined
      }
    }

    let jsonSqlQuotes = `'''`
    if (connection.type === Provider.Snowflake) {
      jsonSqlQuotes = `$$`
    }
    let query = ''

    let outTable = tablenameResult
    let outTableLookup = tablenameLookup
    let copyFromTempTable = ''
    let copyFromTempTableLookup = ''
    if (connection.type === Provider.BigQuery && codeType === WorkflowCodeType.StoredProcedure) {
      outTable = `${connection.tempLocation}.${tempTableName(componentName, preparedParams.source)}`
      outTableLookup = `${outTable}_lookup_table`
      copyFromTempTable = connection.getCreateTempTableQuery(
        tablenameResult,
        `SELECT * FROM ${connection.quoteName(outTable)}`,
        codeType,
        null
      )
      if (preparedParams.nbuckets) {
        copyFromTempTableLookup = connection.getCreateTempTableQuery(
          tablenameLookup,
          `SELECT * FROM ${connection.quoteName(outTableLookup)}`,
          codeType,
          null
        )
      }
    }
    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake: {
        const dropTablesStatement =
          connection.type === Provider.BigQuery && codeType === WorkflowCodeType.StoredProcedure
            ? ''
            : dedent`
          DROP TABLE IF EXISTS ${connection.quoteName(tablenameResult)};
          DROP TABLE IF EXISTS ${connection.quoteName(tablenameLookup)};
        `

        query = dedent`
          ${dropTablesStatement}
          CALL ${at}.CREATE_SPATIAL_COMPOSITE_UNSUPERVISED(
          ${connection.quoteMultilineString(
            dedent`SELECT ${selectColumnsString} FROM ${connection.quoteName(source)}`
          )},
          '${preparedParams.indexcol}',
          '${outTable}',
          ${jsonSqlQuotes}
          ${JSON.stringify(options, null, 2)}
          ${jsonSqlQuotes});
          ${copyFromTempTable}
          ${copyFromTempTableLookup}
        `
        break
      }
      case Provider.DatabricksRest: {
        query = dedent`
            from carto_statistics.CREATE_SPATIAL_COMPOSITE_UNSUPERVISED import main as create_spatial_composite_unsupervised

            output_table = '${connection.quoteName(tablenameResult)}'
            drop_query = f'DROP TABLE IF EXISTS {output_table}'
            spark.sql(drop_query)
            create_spatial_composite_unsupervised(
              f'SELECT ${selectColumnsString} FROM ${connection.quoteName(source)}',
              '${preparedParams.indexcol as string}',
              output_table,
              **${JSON.stringify(options, null, 2)}
            )
          `
        const optimizeResultTableQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
          connection.optimizationStatsQuery(tablenameResult)
        )
        let optimizeLookupTableQuery = ''
        if (preparedParams.nbuckets) {
          optimizeLookupTableQuery = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
            connection.optimizationStatsQuery(tablenameLookup)
          )
        }
        query = dedent`
          ${query}
          ${optimizeResultTableQuery}
          ${optimizeLookupTableQuery}
        `
        break
      }
      default:
        return null
    }

    return {
      outputs: {
        result: tablenameResult,
        lookup: tablenameLookup
      },
      query
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const name = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    return {
      result: name,
      lookup: `${name}_lookup_table`
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })

    if (resolvedParamValues.scaling && resolvedParamValues.aggregation) {
      const scalingValue = (params.scaling as WorkflowComponentValue).value as string
      const aggregationValue = (params.aggregation as WorkflowComponentValue).value as string
      if (scalingValue === 'STANDARD_SCALER' && aggregationValue === 'GEOMETRIC') {
        problems.push({
          parameter: 'aggregation',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Aggregation method cannot be "GEOMETRIC" when scaling is "STANDARD_SCALER"'
        })
      }
    }

    return problems
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Snowflake:
        return '1.8.0'
      default:
        return '1.0.0'
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({
    workflow,
    params,
    schemas,
    connection,
    componentName
  }: GetOutputSchemasParams): TableSchemasList {
    const columns = [{ name: 'spatial_score', type: ColumnDataType.Number }]
    if (params.indexcol) {
      const schema = this._getSchemaForTablename(params.source as string, schemas, connection)
      if (schema == undefined) {
        return []
      }
      const indexCol = schema.find((col) => col.name === params.indexcol)
      if (indexCol == undefined) {
        return []
      }
      columns.push(indexCol)
    } else {
      columns.push({ name: 'index', type: ColumnDataType.Number })
    }
    const columns_lookup = [
      { name: 'lower_bound', type: ColumnDataType.Number },
      { name: 'upper_bound', type: ColumnDataType.Number },
      { name: 'spatial_score', type: ColumnDataType.Number }
    ]
    const fqn = this._getDryRunOutputTableFqn(workflow, params, connection, componentName, SINGLE_OUTPUT_NAME)

    return [
      { fqn, schema: columns },
      { fqn: `${fqn}_lookup_table`, schema: columns_lookup }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { SpatialCompositeUnsupervised }
